// cleanup_convert_to_typescript_file_PAYMENTS
// cleanup_remove_external_libraries_PAYMENTS

/* istanbul ignore file */
/* eslint-disable import/no-cycle */
import axios from 'axios';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import jsCookie from 'js-cookie';
import { pick, memoize, get } from 'lodash';

// Utils/Constants
import { PAYMENT_MASKED_STORED } from 'components/widgets/payments/web/payments.constants';
import { MAP_FORMAT } from 'components/widgets/wireImports/CreateACHImportMap/utils.mapFormats';
import { DROPDOWN_OPTION_NONE } from 'components/widgets/wireImports/CreateACHImportMap/constants';
import { transformDataToDropdown } from 'components/widgets/wireImports/CreateACHImportMap/utils';
import {
  memoizeWithToken,
  memoizeWithTokenAndParams,
  setTokenAndParamsAsCacheKey
} from 'api/utils/memoizeUtils';
import { HEADERS } from 'constants.js';
import uniqId from 'utils/uniqId';

// TODO: Remove all ACH services from this file after refactor

export function formatPhone(form) {
  let { fax, mobile, phone } = form;
  phone = phone ? phone.replace(/[^\d]/g, '') : phone;
  mobile = mobile ? mobile.replace(/[^\d]/g, '') : mobile;
  fax = fax ? fax.replace(/[^\d]/g, '') : fax;

  return {
    ...form,
    fax,
    mobile,
    phone
  };
}

export const setChallengeTokens = () => {
  const xCMPasscode = jsCookie.get(HEADERS.X_CM_PASSCODE);
  const nextCMPasscode = jsCookie.get(HEADERS.X_NEXTCM_PASSCODE);
  const commonHeaders = axios.defaults.headers.common;
  if (xCMPasscode) commonHeaders[HEADERS.X_CM_PASSCODE] = xCMPasscode;
  if (nextCMPasscode) commonHeaders[HEADERS.X_NEXTCM_PASSCODE] = nextCMPasscode;
};

export const deleteChallengeTokens = () => {
  delete axios.defaults.headers.common[HEADERS.X_CM_PASSCODE];
  delete axios.defaults.headers.common[HEADERS.X_NEXTCM_PASSCODE];
};

export const getPaymentPermissions = memoizeWithTokenAndParams((postData) =>
  memoizeWithToken(() =>
    from(axios.post('/infrastructure/servicepermissions/isServicePermitted', postData)).pipe(
      map(({ data: { data, success } }) => ({ data, success }))
    )
  )
);

export function addContact(postData) {
  const formattedData = formatPhone(postData);
  setChallengeTokens();
  return from(axios.post('/payments/contact/save', formattedData)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function getBankCodes(postData) {
  return from(axios.post('/payments/contact/getBankCodes', postData)).pipe(
    map(({ data: { data } }) =>
      data.map((c) => ({
        address1: c.addressLine1,
        address2: c.addressLine2,
        bankCode: c.bankRouteNumber,
        bankName: c.bankName,
        intermediary1: c.intermediary1,
        intermediary2: c.intermediary2,
        paymentMethod: c.paymentMethod,
        routingNumber: c.bankRouteNumber
      }))
    )
  );
}

export function getTaxFormTypes() {
  return from(axios.post('/payments/getTaxFormTypes')).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}
export function getTaxFormTypeCodes(postData) {
  return from(axios.post('/payments/getTaxFormTypeCodes', postData)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}
export function getTaxConfig(postData) {
  return from(axios.post('/payments/getTaxConfig', postData)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function isValidAba(postData) {
  return from(axios.post('/payments/contact/isValidAba', postData)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function retrieveWireTemplatesByName(postData) {
  return from(axios.post('/payments/retrieveWireTemplatesByName', postData)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function saveACHTransactionDetails(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/saveACHTransactionDetails', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}
// For ACH Templates
export function saveACHTransactionDetails$(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/saveACHTransactionDetails', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function saveACHPaymentTransactionDetails(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/saveACHPaymentTransactionDetails', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}
// For ACH Payments
export function saveACHPaymentTransactionDetails$(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/saveACHPaymentTransactionDetails', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function deleteRecurringInstruction(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/deleteRecurringInstruction', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function updateRecurringInstruction(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/updateRecurringInstruction', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function createRecurringInstruction(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/createRecurringInstruction', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function getAchReversalReasons(payload) {
  return from(axios.post('/payments/achReversal/getReasons', payload)).pipe(
    map(({ data }) => data)
  );
}

export function getAchTemplateSettings(payload) {
  return from(axios.post('/payments/getAchTemplateSettings', payload)).pipe(
    map(({ data }) => data)
  );
}

export function getAchTemplateSettings$(payload) {
  return from(axios.post('/payments/getAchTemplateSettings', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function getImportedBatchDetails(payload) {
  return () =>
    from(axios.post('/payments/getImportedBatchDetails', payload)).pipe(map(({ data }) => data));
}

export function updateACHPaymentTransactionDetails(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/updateACHPaymentTransactionDetails', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}
// For ACH Payments
export function updateACHPaymentTransactionDetails$(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/updateACHPaymentTransactionDetails', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function deleteACHPaymentTransactionDetails(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/deleteACHPaymentTransactionDetails', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}
// For ACH Payments
export function deleteACHPaymentTransactionDetails$(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/deleteACHPaymentTransactionDetails', payload)).pipe(
    map(({ data: { data, result, success } }) => {
      deleteChallengeTokens();
      return { data, result, success };
    })
  );
}

export function activatePayment(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/activatePayment', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function updatePayment(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/updatePayment', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function updatePayment$(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/updatePayment', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function retrievePaymentDetails(payload) {
  return from(axios.post('/payments/retrievePaymentDetails', payload)).pipe(
    map(({ data }) => data)
  );
}

export function initiatePayment(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/initiatePayment', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function initiatePayment$(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/initiatePayment', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function updateACHTransactionDetails(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/updateACHTransactionDetails', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function updateACHTransactionDetails$(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/updateACHTransactionDetails', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function actionPaymentTemplates(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/actionPaymentTemplates', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function actionPaymentTemplates$(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/actionPaymentTemplates', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function getPaymentTemplateDetails(payload) {
  return () =>
    from(axios.post('/payments/getPaymentTemplateDetails', payload)).pipe(
      map(({ data }) => ({
        ...data,
        data: Array.isArray(data.data)
          ? data.data.filter(Boolean).map((item) => ({ ...item, id: item.id || uniqId() }))
          : data.data
      }))
    );
}

export function getPaymentTemplateDetails$(payload) {
  return from(axios.post('/payments/getPaymentTemplateDetails', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function deleteACHTransactionDetails(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/deleteACHTransactionDetails', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function deleteACHTransactionDetails$(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/deleteACHTransactionDetails', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function getPaymentTypesForTemplates() {
  return from(axios.post('/payments/getPaymentTypesForTemplates')).pipe(map(({ data }) => data));
}

export function getPaymentTypesForTemplates$() {
  return from(axios.post('/payments/getPaymentTypesForTemplates')).pipe(map(({ data }) => data));
}

export function getAchCompanyForPaymentType(payload) {
  return () =>
    from(axios.post('/payments/getAchCompanyForPaymentType', payload)).pipe(
      map(({ data }) => data)
    );
}

export function getAchCompanyForPaymentType$(payload) {
  return from(axios.post('/payments/getAchCompanyForPaymentType', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function getAllCountries() {
  return from(axios.post('/payments/getAllCountries')).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function getInternationalACHDestCountries() {
  return from(axios.post('/payments/getInternationalACHDestCountries')).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function getInternationalACHDestCurrencies() {
  return from(axios.post('/payments/getInternationalACHDestCurrencies')).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function getInternationalACHFX() {
  return from(axios.post('/payments/getInternationalACHFX')).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function activateTaxPayment(payload, isSmallBusiness) {
  setChallengeTokens();
  const endpoint = isSmallBusiness ? '/payments/activateTaxPayment' : '/payments/activatePayment';
  return from(axios.post(endpoint, payload)).pipe(
    map(({ data: { data, result, success } }) => {
      deleteChallengeTokens();
      return { data, result, success };
    })
  );
}

export function updatePaymentTemplate({ achPaymentTemplateSaveRequest }) {
  setChallengeTokens();
  return from(
    axios.post(
      '/payments/updatePaymentTemplate',
      pick(achPaymentTemplateSaveRequest, [
        'achSenderId',
        'batchDescription',
        'discretionaryData',
        'fromAccountId',
        'recurring',
        'recurringPaymentInstructionReq',
        'state',
        'templateCode',
        'templateName'
      ])
    )
  ).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function updatePaymentTemplate$(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/updatePaymentTemplate', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function getAchSettings(payload) {
  return from(axios.post('/payments/getAchSettings', payload)).pipe(map(({ data }) => data));
}

export function getContactAchSettings() {
  return from(axios.post('/payments/contact/getAchSettings')).pipe(map(({ data }) => data));
}

// Date settings for ach existing payments
export function getAchSettings$(payload) {
  return from(axios.post('/payments/getAchSettings', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function getACHPaymentSettings(payload) {
  return () =>
    from(axios.post('/payments/getACHPaymentSettings', payload)).pipe(map(({ data }) => data));
}

export function getACHPaymentSettings$(payload) {
  return from(axios.post('/payments/getACHPaymentSettings', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function copyPaymentTemplate(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/copyPaymentTemplate', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function copyPaymentTemplate$(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/copyPaymentTemplate', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function copyAchPayment(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/copyAchPayment', { ...payload, corpPayment: true })).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function copyAchPaymentAsTemplate(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/copyAchPaymentAsTemplate', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function savePaymentTemplate(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/savePaymentTemplate', payload)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function savePaymentTemplate$(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/savePaymentTemplate', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function getPaymentTemplates(payload) {
  return from(axios.post('/payments/getPaymentTemplates', payload)).pipe(map(({ data }) => data));
}

export function getPaymentTemplatesPermissions() {
  return from(axios.post('/payments/getPaymentTemplatesPermission')).pipe(map(({ data }) => data));
}

export function getCreateTemplatePermissions() {
  return from(axios.post('/payments/getCreateTemplatePermissions')).pipe(map(({ data }) => data));
}

export function getList(dateFilters) {
  const payload = {
    unmaskAccount: sessionStorage.getItem(PAYMENT_MASKED_STORED) === 'false',
    ...dateFilters
  };
  return from(axios.post('/payments/getList', payload)).pipe(map(({ data: { data } }) => data));
}

export function getPaymentTypes() {
  return from(axios.post('/payments/getPaymentTypes')).pipe(map(({ data: { data } }) => data));
}

export function getPaymentTypes$() {
  return from(axios.post('/payments/getPaymentTypes')).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function getSMBPaymentTypes$() {
  return from(axios.post('/payments/getSMBPaymentTypes')).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export const retrieveSMBEmployeePaymentDetails$ = (payload) =>
  from(axios.post('/payments/retrieveSMBEmployeePaymentDetails', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );

export const initiateSMBEmployeePayment$ = (payload) => {
  setChallengeTokens();
  return from(axios.post('/payments/initiateSMBEmployeePayment', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
};

export const getWireAccountsCurried = memoizeWithTokenAndParams((body) =>
  memoizeWithToken(() =>
    from(axios.post('/payments/getWireAccounts', body)).pipe(map(({ data: { data } }) => data))
  )
);

export function getWireAccounts(payload) {
  return from(axios.post('/payments/getWireAccounts', payload)).pipe(
    map(({ data: { data } }) => data)
  );
}

export const getACHAccounts = memoizeWithTokenAndParams((body) =>
  memoizeWithToken(() =>
    from(axios.post('/payments/getACHFromAccounts', body)).pipe(map(({ data: { data } }) => data))
  )
);

export function wirePaymentPermissions() {
  return from(axios.post('/payments/wirePaymentPermissions')).pipe(
    map(({ data: { data } }) => data)
  );
}

export function paymentPermissions() {
  return from(axios.post('/payments/paymentPermissions')).pipe(map(({ data: { data } }) => data));
}
export function getpaymentTemplateItemViewGridDetail(payload) {
  return () =>
    from(axios.post('/payments/getAllTransactionDetailsForACHBatch', payload)).pipe(
      map(({ data }) => data)
    );
}

export function getContactList() {
  return from(axios.post('/payments/contact/getList')).pipe(map(({ data: { data } }) => data));
}

export const viewContact = memoizeWithTokenAndParams((postData) =>
  memoizeWithToken(() =>
    from(axios.post('/payments/contact/viewContact', postData)).pipe(
      map(({ data: { data } }) => data)
    )
  )
);

export function createWirePayment(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/createWirePayment', payload)).pipe(
    map(({ data: res }) => {
      deleteChallengeTokens();
      return { ...res };
    })
  );
}

export function createMultiWirePayments(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/createMultiWirePayments', payload)).pipe(
    map(({ data: res }) => {
      deleteChallengeTokens();
      return { ...res };
    })
  );
}

export function modifyWirePayment(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/modifyWirePayment', payload)).pipe(
    map(({ data: { data, result, success } }) => {
      deleteChallengeTokens();
      return { data, result, success };
    })
  );
}

export function createWireTemplate(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/createWireTemplate', payload)).pipe(
    map(({ data: res }) => {
      deleteChallengeTokens();
      return { ...res };
    })
  );
}

export function getWirePreferences(payload) {
  return from(axios.post('/payments/wirePreferences', payload)).pipe(
    map(({ data: { data } }) => data)
  );
}

export function getFxRates(payload) {
  return from(axios.post('/payments/fxRates', payload)).pipe(map(({ data: { data } }) => data));
}

// NOTE: don't use this call for SMB payments since it has corpPayment flag set to true
export function retrieveWirePayment(payload) {
  return from(axios.post('/payments/retrieveWirePayment', { ...payload, corpPayment: true })).pipe(
    map(({ data: { data } }) => data)
  );
}

export function retrieveWireTemplate(payload) {
  return from(axios.post('/payments/retrieveWireTemplate', payload)).pipe(
    map(({ data: { data } }) => data)
  );
}

export function getLoanPayments(payload) {
  return from(axios.post('/payments/retrieveLoanPaymentOrDraw', payload)).pipe(
    map(({ data: { data } }) => data)
  );
}

export function approveSingleWirePayment(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/approveSingleWirePayment', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function modifyWireTemplate(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/modifyWireTemplate', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function approveWirePayments(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/approveWirePayments', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export const isRecurringLoanTxnEnabled = memoize(() =>
  from(axios.post('/payments/loantxn/isRecurringLoanTxnEnabled')).pipe(
    map(({ data: { data } }) => data)
  )
);

export const getPermittedLoanAccts = memoize(
  (postData) =>
    from(axios.post('/payments/loantxn/getPermittedLoanAccts', postData)).pipe(
      map(({ data: { data } }) => data)
    ),
  (postData) => setTokenAndParamsAsCacheKey(postData)
);

export const getPermittedFundingAccts = memoize(
  (postData) =>
    from(axios.post('/payments/loantxn/getPermittedFundingAccts', postData)).pipe(
      map(({ data: { data } }) => data)
    ),
  (postData) => setTokenAndParamsAsCacheKey(postData)
);

export const getPermittedAccts = memoize((postData) =>
  from(axios.post('/payments/loantxn/getPermittedAccts', postData)).pipe(
    map(({ data: { data } }) => data)
  )
);

export const getPermittedAcctsPartial = memoize((postData) =>
  memoize(() =>
    from(axios.post('/payments/loantxn/getPermittedAccts', postData)).pipe(
      map(({ data: { data } }) => data)
    )
  )
);

export const getValidBusinessDate = () =>
  from(axios.post('/payments/loantxn/getValidBusinessDate')).pipe(
    map(({ data: { data } }) => data)
  );

export const getLoanCalendar = () =>
  from(axios.post('/payments/loantxn/getLoanCalendar')).pipe(map(({ data: { data } }) => data));

export const getPaymentOptions = memoize(
  (postData) =>
    from(axios.post('/payments/loantxn/getPaymentOptions', postData)).pipe(
      map(({ data: { data } }) => data)
    ),
  (postData) => setTokenAndParamsAsCacheKey(postData)
);

export const intiateLoanTxn = memoize((postData) =>
  from(axios.post('/payments/loantxn/intiateLoanTxn', postData)).pipe(
    map(({ data: { data, result, success } }) => ({ data, result, success }))
  )
);

export const isDepositAccountExists = memoizeWithToken(() =>
  from(axios.post('/payments/loantxn/isDepositAccountExists')).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  )
);

export const getNoteNumbers = (payload) =>
  from(axios.post('/payments/loantxn/noteNumbers', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );

export const createLoanTemplate = (payload) =>
  from(axios.post('/payments/loantemplate/createLoanTemplate', payload)).pipe(
    map(({ data: { data, result, success } }) => ({ data, success, result }))
  );

export const modifyLoanTemplate = (payload) =>
  from(axios.post('/payments/loantemplate/modifyLoanTemplate', payload)).pipe(
    map(({ data: { data, result, success } }) => ({ data, success, result }))
  );

export const deleteLoanInstruction = (payload) =>
  from(axios.post('/payments/loantemplate/deleteLoanInstruction', payload)).pipe(
    map(({ data: { data, result, success } }) => ({ data, success, result }))
  );

export const modifyLoanInstruction = (payload) =>
  from(axios.post('/payments/loantemplate/modifyLoanInstruction', payload)).pipe(
    map(({ data: { data, result, success } }) => ({ data, success, result }))
  );

export const createLoanInstruction = (payload) =>
  from(axios.post('/payments/loantemplate/createLoanInstruction', payload)).pipe(
    map(({ data: { data, result, success } }) => ({ data, success, result }))
  );

export const deleteLoanTemplate = (payload) =>
  from(axios.post('/payments/loantemplate/deleteLoanTemplate', payload)).pipe(
    map(({ data: { data, result, success } }) => ({ data, success, result }))
  );

export const initiateLoanTemplate = (payload) =>
  from(axios.post('/payments/loantemplate/intiateLoanTxn', payload)).pipe(
    map(({ data: { data, result, success } }) => ({ data, success, result }))
  );

export function deleteWirePayments(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/deleteWirePayments', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function rejectWirePayments(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/rejectWirePayments', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export const getAvailableBusinessDays = memoize(
  (payload) =>
    from(axios.post('/payments/availableBusinessDays', payload)).pipe(
      map(
        ({
          data: {
            data: { dates = [] }
          }
        }) => dates
      )
    ),
  (payload) => setTokenAndParamsAsCacheKey(payload)
);

export function actionImportedBatch(payload) {
  return from(axios.post('/payments/actionImportedBatch', payload)).pipe(
    map(({ data: { data } }) => data)
  );
}

export function getImportedBatches(payload) {
  return from(
    axios.post('/payments/getImportedBatches', {
      ...payload,
      batchesType: 'Nacha'
    })
  ).pipe(map(({ data: { data } }) => data));
}

export function importFile(payload, query) {
  return from(
    axios.post('/payments/importFile', payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: query
    })
  ).pipe(map(({ data: { data, success } }) => ({ data, success })));
}

export function achCreatePermissions() {
  return from(axios.post('/payments/achCreatePermissions')).pipe(
    map(({ data: { data } }) => data)
  );
}

export function getACHSenders() {
  return from(axios.post('/payments/getACHSenders')).pipe(map(({ data: { data } }) => data));
}

export const getACHImportMaps = () =>
  from(axios.post('/payments/getACHImportMaps')).pipe(map(({ data: { data } }) => data));

export function getACHImportUserMap(payload) {
  return from(axios.post('/payments/getACHImportUserMap', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function approvePayee(payload) {
  setChallengeTokens();
  return from(axios.post('/payments/contact/approve', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function actionPayments({ asPromise, ...payload }) {
  setChallengeTokens();

  if (asPromise) {
    return axios.post('/payments/actionPayments', payload).then(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    });
  }
  return from(axios.post('/payments/actionPayments', payload)).pipe(
    map(({ data: { data, success } }) => {
      deleteChallengeTokens();
      return { data, success };
    })
  );
}

export function deletePayeeAPI(postData) {
  setChallengeTokens();
  return from(axios.post('/payments/contact/delete', postData)).pipe(
    map(({ data: { data } }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export function listWireImportMaps(postData) {
  return from(axios.post('/payments/listWireImportMaps', postData)).pipe(
    map(({ data: { data } }) => data)
  );
}

export function uploadWirePayments({ params, payload }) {
  return from(
    axios.post('/payments/uploadWirePayments', payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params
    })
  ).pipe(map(({ data: { data, success } }) => ({ data, success })));
}

export function uploadedWireFileStatus(postData) {
  return from(axios.post('/payments/uploadedWireFileStatus', postData)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function confirmWireUpload(postData) {
  return from(axios.post('/payments/confirmWireUpload', postData)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function retrieveWireImportSummary(postData) {
  return from(axios.post('/payments/retrieveWireImportSummary', postData)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function getWireTypes(postData) {
  const transformToRadioButtonStructure = (data) =>
    data.map((item) => ({ label: item.value, value: item.code }));

  return from(axios.post('/payments/getWireTypes', postData)).pipe(
    map(({ data: { data } }) => ({
      radio: {
        preselected: data && data[0].code,
        source: transformToRadioButtonStructure(data)
      },
      source: data
    }))
  );
}

export function getDelimiters(postData) {
  const transformToDropdownStructure = (data) =>
    data.map((item) => ({ id: item.code, name: `${item.value} ( ${item.code} )` }));

  return from(axios.post('/payments/getDelimiters', postData)).pipe(
    map(({ data: { data } }) => ({
      dropdown: {
        source: transformToDropdownStructure(data)
      },
      source: data
    }))
  );
}

export function getACHDelimiters(postData) {
  return from(axios.post('/payments/getACHMapDelimiters', postData)).pipe(
    map(({ data: { data } }) => ({
      dropdown: {
        source: transformDataToDropdown.achDelimiters(data)
      },
      source: data
    }))
  );
}

export function getEndOfRecords(postData) {
  return from(axios.post('/payments/getACHMapEndOfRecordOptions', postData)).pipe(
    map(({ data: { data } }) => ({
      dropdown: {
        source: transformDataToDropdown.endOfRecords(data)
      },
      source: data
    }))
  );
}

export function getTransactionCodes(postData) {
  return from(axios.post('/payments/getTranCodesWithDesc', postData)).pipe(
    map(({ data: { data } }) => {
      const apiTransactionCodesOptions = transformDataToDropdown.transactionCodes(data);
      const readyOptions = [DROPDOWN_OPTION_NONE, ...apiTransactionCodesOptions];

      return {
        dropdown: {
          source: readyOptions
        },
        source: readyOptions
      };
    })
  );
}

export function getDelimitersData() {
  return from(axios.post('/payments/getDelimiters')).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}
export function getCharges() {
  const transformToDropdownStructure = (data) =>
    data.map((item) => ({ id: item.code, name: `${item.value} ( ${item.code} )` }));

  return from(axios.post('/payments/getCharges')).pipe(
    map(({ data: { data } }) => ({
      dropdown: {
        source: transformToDropdownStructure(data)
      },
      source: data
    }))
  );
}

export function getEndOfRecordOptions(postData) {
  const mapping = {
    'ccr/lf': 'CR/LF',
    llf: 'LF'
  };

  const transformToDropdownStructure = (data) =>
    data.map((item) => ({
      id: item.code,
      name: `${item.value} ( ${mapping[item.code.toLowerCase()] || item.code} )`
    }));

  return from(axios.post('/payments/getEndOfRecordOptions', postData)).pipe(
    map(({ data: { data } }) => ({
      dropdown: {
        source: transformToDropdownStructure(data)
      },
      source: data
    }))
  );
}

export function getSeparators(postData) {
  const transformToDropdownStructure = (data) => data.map((name) => ({ id: name, name }));

  return from(axios.post('/payments/getSeperators', postData)).pipe(
    map(({ data: { data } }) => ({
      dropdown: {
        source: transformToDropdownStructure(data)
      },
      source: data
    }))
  );
}

export const getWireImportDefaultFields = (postData) =>
  from(axios.post('/payments/getWireImportDefaultFields', postData)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );

export const getDateFormats = () => {
  const transformToDropdownStructure = (data) => data.map((name, k) => ({ id: k + 1, name }));

  return from(axios.post('/payments/getDateFormats')).pipe(
    map(({ data: { data } }) => ({
      dropdown: {
        source: transformToDropdownStructure(data)
      },
      source: data
    }))
  );
};

export function isDuplicateMapName(postData) {
  return from(axios.post('/payments/isDuplicateMapName', postData)).pipe(
    map(({ data: { data, success } }) => ({
      duplicate: data && data.duplicateMapName,
      name: 'mapName',
      success,
      value: postData.mapName
    }))
  );
}

export function isValidAccountNumber({ accountNumber, name } = {}) {
  return from(axios.post('/payments/isValidAccountNumber', { accountNumber })).pipe(
    map(({ data: { data, success } }) => ({
      name,
      success,
      valid: data && data.isValidAccountNumber,
      value: accountNumber
    }))
  );
}

export function isValidBankCode({ beneficiaryBankCode, name }) {
  return from(axios.post('/payments/isValidBankCode', { beneficiaryBankCode })).pipe(
    map(({ data: { data, success } }) => ({
      name,
      success,
      valid: data && data.isValidBenificiaryBankCode,
      value: beneficiaryBankCode
    }))
  );
}

export function isValidDate({ date, dateFormat, name } = {}) {
  return from(axios.post('/payments/isValidDate', { date, dateFormat })).pipe(
    map(({ data: { data, success } }) => ({
      name,
      success,
      valid: get(data, '0.code') ? data[0] : data.isValidDate,
      value: date
    }))
  );
}

export function createWireImportMap(postData) {
  return from(axios.post('/payments/createWireImportMap', postData)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function modifyWireImportMap(postData) {
  return from(axios.post('/payments/modifyWireImportMap', postData)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function sendAchTotals(data) {
  setChallengeTokens();
  return from(axios.post('/payments/ach/totals/send', data)).pipe(
    map(({ data }) => {
      deleteChallengeTokens();
      return data;
    })
  );
}

export const getImportFileResult = (pendingNumber) =>
  from(axios.post(`/payments/getImportFileResult?pendingNumber=${pendingNumber}`)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );

export function getPayeePaymentTypes() {
  return from(axios.post('/payments/contact/getPayeePaymentTypes')).pipe(map(({ data }) => data));
}

export function getImportPermissions() {
  return from(axios.post('/payments/getImportPermissions')).pipe(map(({ data }) => data));
}

export function validateMapName(mapName) {
  return from(axios.post('/payments/isMapNameExist', { mapName })).pipe(map(({ data }) => data));
}

// api/payments/delimitedMap/createACHImportMap
// api/payments/fixedMap/createACHImportMap
// api/payments/delimitedMap/modifyACHImportMap
// api/payments/fixedMap/modifyACHImportMap

export function createACHMap(data, mapFormat) {
  return from(
    axios.post(
      `/payments/${
        mapFormat === MAP_FORMAT.DELIMITED ? 'delimitedMap' : 'fixedMap'
      }/createACHImportMap`,
      data
    )
  ).pipe(map(({ data }) => data));
}

export function modifyACHMap(data, mapFormat) {
  return from(
    axios.post(
      `/payments/${
        mapFormat === MAP_FORMAT.DELIMITED ? 'delimitedMap' : 'fixedMap'
      }/modifyACHImportMap`,
      data
    )
  ).pipe(map(({ data }) => data));
}

export function retrieveACHImportMap(mapCode) {
  return from(axios.post('/payments/retrieveACHImportMap', { mapCode })).pipe(
    map(({ data }) => data)
  );
}

export function getAvailableAccountTypes() {
  const baseOptions = [
    DROPDOWN_OPTION_NONE,
    { id: 'checking', name: 'Checking' },
    { id: 'saving', name: 'Saving' }
  ];

  const additionalOptions = [
    { id: 'loan', name: 'Loan' },
    { id: 'generalLedger', name: 'General Ledger' }
  ];

  return from(axios.post('/payments/getAvailableAccountTypes')).pipe(
    map(({ data: { data } }) => {
      const availableAdditionalOptions = additionalOptions.filter((option) => data[option.id]);

      const readyData = [...baseOptions, ...availableAdditionalOptions];

      return {
        dropdown: {
          source: readyData
        },
        source: readyData
      };
    })
  );
}

export const retrieveLoanTemplate = (payload) =>
  from(axios.post('/payments/retrieveLoanTemplate', payload)).pipe(
    map(({ data: { data } }) => data)
  );

export const validateLoanTemplateName = (templateName) =>
  from(axios.post('/payments/isValidLoanTemplateName', { templateName })).pipe(
    map(({ data: { success } }) => success)
  );

export const getPaymentsPreferences = () =>
  from(axios.post('/payments/preferences')).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );

export const getMultipleACHTemplateData = (payload) =>
  from(axios.post('/payments/getMultipleACHTemplateData', payload)).pipe(map(({ data }) => data));

export const createMultipleACHPayments = (payload) =>
  from(axios.post('/payments/activateMultipleAchPayments', payload)).pipe(map(({ data }) => data));

export const getSMBLoanTemplates = (payload) =>
  from(axios.post('/payments/getSMBLoanTemplates', payload)).pipe(
    map(({ data: { data } }) => data)
  );
